import { useMutation, useQueryClient } from "@tanstack/react-query";

import {
  apiErrorHandler,
  getBackendErrorMessage,
} from "@/core/api/api-error-handler";
import { mutateData } from "@/core/api/api-methods";
import {
  invalidateAfterMutation,
  payrollBatchCacheKeys,
} from "@/core/api/invalidate-after-mutation";
import { logger } from "@/core/utils/func/logger";
import { payrollEndpoints } from "@/features/payroll/api/endpoints";
import type { PayrollBatchSimulateResponseDto } from "@/features/payroll/api/dto";

/**
 * Simule une paie (`POST /PayrollBatches/{id}/simulate`). Aucun mouvement
 * d'argent : chaque salarié est vérifié auprès de wallet-service (membre :
 * portefeuille actif ; non-membre : code de retrait dans la limite du plafond),
 * les frais calculés, et le total à débiter comparé au solde disponible. La
 * paie passe à `SIMULATED` avec `canLaunch`, `sufficientBalance`,
 * `simulationExpiresAt`.
 */
export const useSimulatePayrollBatch = (batchId: string) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: ["payroll", "payrollBatches", "simulate", batchId],
    mutationFn: () =>
      mutateData({
        url: payrollEndpoints.payrollBatches.simulate(batchId),
        method: "POST",
      }) as unknown as Promise<PayrollBatchSimulateResponseDto>,
    onSuccess: () =>
      invalidateAfterMutation(queryClient, payrollBatchCacheKeys(batchId)),
    onError: (error) => {
      const message = getBackendErrorMessage(error) ?? apiErrorHandler(error);
      logger.error(
        `useSimulatePayrollBatch failed: ${message}`,
        error instanceof Error ? error : undefined,
        { prefix: "payroll" }
      );
    },
  });
};
